import './MonthCollapse.scss';
import { formatDate, formatNumber } from '../../../core/helpers';
import Collapse from '../../../components/Collapse/Collapse';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { SellerRoutesEnum } from '../../../core/enums';
import Button from '../../../components/Button';

export interface MonthItem {
  'Storage costs': {
    monthly: number;
    average: number;
    total: number;
  };
  'Fulfillment cost': {
    fba: number;
    service_addons: number;
    variable_closing_fee: number;
    total: number;
  };
  'Amazon fees': {
    referal: number;
    fixed: number;
    variable_closing: number;
    total: number;
  };
  Other: {
    goods_sold: number;
    total: number;
    miscellaneous_cost: number;
  };
}

interface Props {
  data: MonthItem & { date: string; total: number };
  className?: string;
  isOpened: boolean;
  onCollpaseBtnClick: () => void;
}

export default function MonthCollapse({
  data,
  className = '',
  isOpened,
  onCollpaseBtnClick,
}: Props) {
  const { t } = useTranslation();
  const indicator = Object.entries(data)
    .map(([k, v]) => {
      if (k === 'date' || k === 'total') return null;
      return v.total;
    })
    .filter((s) => s !== null);

  return (
    <Collapse
      className={`MonthCollapse py-3 ${className}`}
      externalControl={{
        isOpened,
        onTriggerClick: onCollpaseBtnClick,
      }}
      triggerRenderer={(_, toggle) => {
        return (
          <div onClick={toggle} className='px-3 cursor-pointer'>
            <div className='d-flex align-items-center justify-content-between Text--20 fw-800 mb-2'>
              <div>{formatDate(new Date(data.date), 'MMMM')}</div>
              <div>
                {formatNumber(data.total / 100, {
                  currency: 'eur',
                })}
              </div>
            </div>
            <div className='MonthCollapse__indicator d-flex overflow-hidden'>
              {indicator.map((v, i) => (
                <div
                  key={i}
                  className={`MonthCollapse__color--${i + 1}`}
                  style={{
                    width: `${Math.round((v / data.total) * 100)}%`,
                  }}
                />
              ))}
            </div>
          </div>
        );
      }}
      contentRenderer={(ref, toggle) => {
        return (
          <div className='pt-3 pb-2 px-3' ref={ref}>
            <div className='MonthCollapse__separator mx-n3 mb-2' />
            <div className='d-flex align-items-center justify-content-between mb-2'>
              <div className='d-flex align-items-center'>
                <div className='MonthCollapse__circle me-1 rounded-circle MonthCollapse__color--1' />
                <div className='fw-600'>{t('Storage costs')}</div>
              </div>
              <div className='fw-600'>
                {formatNumber(data['Storage costs'].total / 100, {
                  currency: 'eur',
                })}
              </div>
            </div>
            <div className='d-flex align-items-center justify-content-between mb-2'>
              <div>{t('FBA fulfillment fees')}</div>
              <div>
                {formatNumber(data['Storage costs'].monthly / 100, {
                  currency: 'eur',
                })}
              </div>
            </div>
            <div className='d-flex align-items-center justify-content-between mb-2'>
              <div>{t('Average inventory units stored')}</div>
              <div>
                {formatNumber(data['Storage costs'].average / 100, {
                  currency: 'eur',
                })}
              </div>
            </div>
            <Link
              className='text-decoration-none Link--blue fw-600'
              to={SellerRoutesEnum.Inventory + '?type=fees'}
            >
              {t('Show fee per product')}
            </Link>
            <div className='MonthCollapse__separator mx-n3 mt-3 mb-2' />
            <div className='d-flex align-items-center justify-content-between mb-2'>
              <div className='d-flex align-items-center'>
                <div className='MonthCollapse__circle me-1 rounded-circle MonthCollapse__color--2' />
                <div className='fw-600'>{t('Fulfillment cost')}</div>
              </div>
              <div className='fw-600'>
                {formatNumber(data['Fulfillment cost'].total / 100, {
                  currency: 'eur',
                })}
              </div>
            </div>
            <div className='d-flex align-items-center justify-content-between mb-2'>
              <div>{t('FBA fulfillment fees')}</div>
              <div>
                {formatNumber(data['Fulfillment cost'].fba / 100, {
                  currency: 'eur',
                })}
              </div>
            </div>
            <div className='d-flex align-items-center justify-content-between mb-2'>
              <div>{t('Service add-ons')}</div>
              <div>
                {formatNumber(data['Fulfillment cost'].service_addons / 100, {
                  currency: 'eur',
                })}
              </div>
            </div>
            <div className='d-flex align-items-center justify-content-between mb-2'>
              <div>{t('Variable Closing Fee')}</div>
              <div>
                {formatNumber(
                  data['Fulfillment cost'].variable_closing_fee / 100,
                  {
                    currency: 'eur',
                  }
                )}
              </div>
            </div>
            <Link
              className='text-decoration-none Link--blue fw-600'
              to={SellerRoutesEnum.Inventory + '?type=fees'}
            >
              {t('Show fee per product')}
            </Link>
            <div className='MonthCollapse__separator mx-n3 mt-3 mb-2' />
            <div className='d-flex align-items-center justify-content-between mb-2'>
              <div className='d-flex align-items-center'>
                <div className='MonthCollapse__circle me-1 rounded-circle MonthCollapse__color--3' />
                <div className='fw-600'>{t('Amazon fees')}</div>
              </div>
              <div className='fw-600'>
                {formatNumber(data['Amazon fees'].total / 100, {
                  currency: 'eur',
                })}
              </div>
            </div>
            <div className='d-flex align-items-center justify-content-between mb-2'>
              <div>{t('Referral Fee')}</div>
              <div>
                {formatNumber(data['Amazon fees'].referal / 100, {
                  currency: 'eur',
                })}
              </div>
            </div>
            <div className='d-flex align-items-center justify-content-between mb-2'>
              <div>{t('Fixed Closing Fee')}</div>
              <div>
                {formatNumber(data['Amazon fees'].fixed / 100, {
                  currency: 'eur',
                })}
              </div>
            </div>
            <div className='d-flex align-items-center justify-content-between mb-2'>
              <div>{t('Variable Closing Fee')}</div>
              <div>
                {formatNumber(data['Amazon fees'].variable_closing / 100, {
                  currency: 'eur',
                })}
              </div>
            </div>
            <Button styling='naked-blue' className='fw-600'>
              {t('Show fee per transaction')}
            </Button>
            <div className='MonthCollapse__separator mx-n3 mt-3 mb-2' />
            <div className='d-flex align-items-center justify-content-between mb-2'>
              <div className='d-flex align-items-center'>
                <div className='MonthCollapse__circle me-1 rounded-circle MonthCollapse__color--3' />
                <div className='fw-600'>{t('Other')}</div>
              </div>
              <div className='fw-600'>
                {formatNumber(data['Other'].total / 100, {
                  currency: 'eur',
                })}
              </div>
            </div>
            <div className='d-flex align-items-center justify-content-between mb-2'>
              <div>{t('Cost of goods sold')}</div>
              <div>
                {formatNumber(data['Other'].goods_sold / 100, {
                  currency: 'eur',
                })}
              </div>
            </div>
            <div className='d-flex align-items-center justify-content-between mb-2'>
              <div>{t('Miscellaneous cost')}</div>
              <div>
                {formatNumber(data['Other'].miscellaneous_cost / 100, {
                  currency: 'eur',
                })}
              </div>
            </div>
            <Button
              styling='naked-blue'
              className='fw-600 mt-4'
              onClick={toggle}
            >
              {t('Hide details')}
            </Button>
          </div>
        );
      }}
    />
  );
}
